/* Amatro theme — vanilla JS, no dependencies.
   Components register as custom elements so they re-initialise
   whenever the theme editor re-renders a section. */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ------------------------------------------------------------------
     Announcement bar: rotates its messages, pauses on hover/focus,
     shows every message statically when the visitor prefers reduced motion.
     ------------------------------------------------------------------ */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('[data-announcement-item]'));
      if (this.items.length < 2) return;

      this.index = 0;
      this.interval = Math.max(2000, parseInt(this.dataset.interval, 10) || 4000);
      this.wide = window.matchMedia('(min-width: 48em)');

      this.onPause = () => this.pause();
      this.onPlay = () => this.play();
      this.onVisibility = () => (document.hidden ? this.pause() : this.play());

      this.addEventListener('mouseenter', this.onPause);
      this.addEventListener('mouseleave', this.onPlay);
      this.addEventListener('focusin', this.onPause);
      this.addEventListener('focusout', this.onPlay);
      document.addEventListener('visibilitychange', this.onVisibility);
      reduceMotion.addEventListener('change', this.onPlay);
      this.wide.addEventListener('change', this.onPlay);

      this.play();
    }

    disconnectedCallback() {
      this.pause();
      document.removeEventListener('visibilitychange', this.onVisibility);
      reduceMotion.removeEventListener('change', this.onPlay);
      if (this.wide) this.wide.removeEventListener('change', this.onPlay);
    }

    get isStatic() {
      return reduceMotion.matches && this.wide.matches;
    }

    play() {
      this.pause();
      if (this.isStatic) {
        this.items.forEach((item) => this.setActive(item, true));
        return;
      }
      this.show(this.index);
      this.timer = window.setInterval(() => {
        this.show((this.index + 1) % this.items.length);
      }, this.interval);
    }

    pause() {
      window.clearInterval(this.timer);
    }

    show(index) {
      this.index = index;
      this.items.forEach((item, i) => this.setActive(item, i === index));
    }

    setActive(item, active) {
      item.classList.toggle('is-active', active);
      if (active) {
        item.removeAttribute('aria-hidden');
      } else {
        item.setAttribute('aria-hidden', 'true');
      }
      item.querySelectorAll('a').forEach((link) => {
        link.tabIndex = active ? 0 : -1;
      });
    }
  }

  /* ------------------------------------------------------------------
     Header: mega menu (hover + click + keyboard), mobile drawer, search.
     ------------------------------------------------------------------ */
  class SiteHeader extends HTMLElement {
    connectedCallback() {
      this.desktop = window.matchMedia('(min-width: 64em)');
      this.setupNav();
      this.setupDrawer();
      this.setupSearch();

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeAllPanels();
      };
      this.onDesktopChange = (event) => {
        if (event.matches) {
          this.closeDrawer();
        } else {
          this.closeAllPanels();
        }
      };
      document.addEventListener('click', this.onDocumentClick);
      this.desktop.addEventListener('change', this.onDesktopChange);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      this.desktop.removeEventListener('change', this.onDesktopChange);
      this.unlockScroll();
    }

    /* --- Desktop navigation panels --- */
    setupNav() {
      this.navItems = Array.from(this.querySelectorAll('[data-nav-item]'));
      this.navItems.forEach((item) => {
        const toggle = item.querySelector('[data-nav-toggle]');
        const panel = item.querySelector('[data-nav-panel]');
        if (!toggle || !panel) return;

        let closeTimer = 0;
        const open = () => {
          this.closeAllPanels(item);
          item.classList.add('is-open');
          toggle.setAttribute('aria-expanded', 'true');
        };
        const close = () => {
          item.classList.remove('is-open');
          toggle.setAttribute('aria-expanded', 'false');
        };
        item.closePanel = close;

        toggle.addEventListener('click', () => {
          if (item.classList.contains('is-open')) {
            close();
          } else {
            open();
          }
        });
        item.addEventListener('mouseenter', () => {
          if (!this.desktop.matches) return;
          window.clearTimeout(closeTimer);
          open();
        });
        item.addEventListener('mouseleave', () => {
          if (!this.desktop.matches) return;
          closeTimer = window.setTimeout(close, 160);
        });
        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) close();
        });
        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && item.classList.contains('is-open')) {
            event.preventDefault();
            close();
            toggle.focus();
          }
        });
      });
    }

    closeAllPanels(except) {
      (this.navItems || []).forEach((item) => {
        if (item !== except && item.closePanel) item.closePanel();
      });
    }

    /* --- Mobile drawer --- */
    setupDrawer() {
      this.burger = this.querySelector('[data-menu-toggle]');
      this.drawer = this.querySelector('[data-menu-drawer]');
      if (!this.burger || !this.drawer) return;

      this.burger.addEventListener('click', () => {
        if (this.drawer.hidden) {
          this.openDrawer();
        } else {
          this.closeDrawer();
        }
      });
      this.drawer.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeDrawer();
          this.burger.focus();
        }
      });
    }

    openDrawer() {
      this.closeSearch();
      const rect = this.getBoundingClientRect();
      this.style.setProperty('--drawer-top', `${Math.max(0, rect.bottom)}px`);
      this.drawer.hidden = false;
      this.burger.setAttribute('aria-expanded', 'true');
      if (this.burger.dataset.closeLabel) this.burger.setAttribute('aria-label', this.burger.dataset.closeLabel);
      this.lockScroll();
      const first = this.drawer.querySelector('a, summary, button, input');
      if (first) first.focus();
    }

    closeDrawer() {
      if (!this.drawer || this.drawer.hidden) return;
      this.drawer.hidden = true;
      this.burger.setAttribute('aria-expanded', 'false');
      if (this.burger.dataset.openLabel) this.burger.setAttribute('aria-label', this.burger.dataset.openLabel);
      this.unlockScroll();
    }

    /* --- Search panel --- */
    setupSearch() {
      this.searchToggle = this.querySelector('[data-search-toggle]');
      this.searchPanel = this.querySelector('[data-search-panel]');
      if (!this.searchToggle || !this.searchPanel) return;

      this.searchInput = this.searchPanel.querySelector('[data-search-input]');
      this.searchToggle.addEventListener('click', () => {
        if (this.searchPanel.hidden) {
          this.openSearch();
        } else {
          this.closeSearch();
        }
      });
      const closeButton = this.searchPanel.querySelector('[data-search-close]');
      if (closeButton) {
        closeButton.addEventListener('click', () => {
          this.closeSearch();
          this.searchToggle.focus();
        });
      }
      this.searchPanel.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.closeSearch();
          this.searchToggle.focus();
        }
      });
    }

    openSearch() {
      this.closeDrawer();
      this.closeAllPanels();
      this.searchPanel.hidden = false;
      this.searchToggle.setAttribute('aria-expanded', 'true');
      if (this.searchInput) this.searchInput.focus();
    }

    closeSearch() {
      if (!this.searchPanel || this.searchPanel.hidden) return;
      this.searchPanel.hidden = true;
      this.searchToggle.setAttribute('aria-expanded', 'false');
    }

    lockScroll() {
      document.documentElement.classList.add('is-locked');
    }

    unlockScroll() {
      document.documentElement.classList.remove('is-locked');
    }
  }

  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);
  if (!customElements.get('site-header')) customElements.define('site-header', SiteHeader);

  /* ------------------------------------------------------------------
     Fade-in on scroll: add `fade-in` to any element.
     ------------------------------------------------------------------ */
  let observer = null;

  function revealAll(root) {
    root.querySelectorAll('.fade-in').forEach((el) => el.classList.add('is-visible'));
  }

  function initFadeIn(root = document) {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }
    if (!observer) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          });
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      );
    }
    const viewportBottom = window.innerHeight;
    root.querySelectorAll('.fade-in:not(.is-visible)').forEach((el) => {
      /* Above the fold on load: show immediately, no entrance animation */
      if (el.getBoundingClientRect().top < viewportBottom) {
        el.classList.add('fade-in--instant', 'is-visible');
        return;
      }
      observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initFadeIn());
  } else {
    initFadeIn();
  }
  document.addEventListener('shopify:section:load', (event) => initFadeIn(event.target));
})();

/* ==================================================================
   Commerce components: rotating word, product form (quick add + PDP),
   variant picker, quantity input, cart helpers.
   ================================================================== */
(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const strings = (window.theme && window.theme.strings) || {};
  const routes = (window.theme && window.theme.routes) || { cartAdd: '/cart/add.js', cartJs: '/cart.js', cart: '/cart' };

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
    });
  }

  /* ---------- Rotating word in the hero H1 ---------- */
  class RotatingWord extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.rotating-word__item'));
      if (this.items.length < 2 || reduceMotion.matches) return;
      this.index = 0;
      this.interval = Math.max(1500, parseInt(this.dataset.interval, 10) || 2500);
      this.onVisibility = () => (document.hidden ? this.stop() : this.start());
      document.addEventListener('visibilitychange', this.onVisibility);
      this.start();
    }

    disconnectedCallback() {
      this.stop();
      document.removeEventListener('visibilitychange', this.onVisibility);
    }

    start() {
      this.stop();
      this.timer = window.setInterval(() => this.next(), this.interval);
    }

    stop() {
      window.clearInterval(this.timer);
    }

    next() {
      const current = this.items[this.index];
      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      current.setAttribute('aria-hidden', 'true');
      window.setTimeout(() => current.classList.remove('is-leaving'), 500);

      this.index = (this.index + 1) % this.items.length;
      const next = this.items[this.index];
      next.classList.add('is-active');
      next.removeAttribute('aria-hidden');
    }
  }

  /* ---------- Product form: add to cart without leaving the page ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form || !window.fetch) return;
      this.button = this.form.querySelector('[data-add-to-cart]');
      this.label = this.button ? this.button.querySelector('[data-add-label]') : null;
      this.status = this.form.querySelector('[data-form-status]');
      this.form.addEventListener('submit', (event) => this.submit(event));
    }

    setStatus(message, type) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
      this.status.classList.toggle('form-status--success', type === 'success');
      this.status.classList.toggle('form-status--error', type === 'error');
    }

    async submit(event) {
      event.preventDefault();
      if (this.button && this.button.disabled) return;
      const originalLabel = this.label ? this.label.textContent : '';
      if (this.button) this.button.disabled = true;
      this.setStatus('');

      try {
        const response = await fetch(routes.cartAdd, {
          method: 'POST',
          body: new FormData(this.form),
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || strings.addError);

        const cart = await fetch(routes.cartJs, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        updateCartCount(cart.item_count);
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

        if (this.label) {
          this.label.textContent = strings.added || 'Added';
          window.setTimeout(() => {
            this.label.textContent = originalLabel;
          }, 2000);
        }
        this.setStatus(strings.addedMessage, 'success');
      } catch (error) {
        this.setStatus(error.message || strings.addError, 'error');
      } finally {
        if (this.button) this.button.disabled = false;
      }
    }
  }

  /* ---------- Variant picker (radio pills) ---------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const data = this.querySelector('[data-variants]');
      this.form = document.getElementById(this.dataset.formId);
      if (!data || !this.form) return;

      this.variants = JSON.parse(data.textContent);
      this.idInput = this.form.querySelector('input[name="id"]');
      this.button = this.form.querySelector('[data-add-to-cart]');
      this.label = this.button ? this.button.querySelector('[data-add-label]') : null;
      this.priceEls = Array.from(
        document.querySelectorAll(
          `[data-product-price="${this.dataset.productId}"], [data-product-price-sticky="${this.dataset.productId}"]`
        )
      );
      this.vipEls = Array.from(document.querySelectorAll(`[data-product-vip-price="${this.dataset.productId}"]`));
      this.fieldsets = Array.from(this.querySelectorAll('fieldset'));

      this.addEventListener('change', () => this.update());
      this.update();
    }

    get selection() {
      return this.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('input:checked');
        return checked ? checked.value : null;
      });
    }

    update() {
      const selection = this.selection;
      const variant = this.variants.find((v) => v.options.every((value, i) => value === selection[i]));

      this.fieldsets.forEach((fieldset, i) => {
        fieldset.querySelectorAll('input').forEach((input) => {
          const available = this.variants.some(
            (v) =>
              v.available &&
              v.options[i] === input.value &&
              v.options.every((value, j) => j === i || value === selection[j])
          );
          const pill = input.closest('.pill');
          if (pill) pill.classList.toggle('pill--unavailable', !available);
        });
      });

      if (!variant) {
        this.setButton(false, strings.unavailable);
        return;
      }

      if (this.idInput) this.idInput.value = variant.id;
      this.priceEls.forEach((el) => {
        el.textContent = variant.price;
      });
      if (variant.vip_price) {
        this.vipEls.forEach((el) => {
          el.textContent = variant.vip_price;
        });
      }
      this.setButton(variant.available, variant.available ? strings.addToCart : strings.soldOut);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }

    setButton(enabled, text) {
      if (!this.button) return;
      this.button.disabled = !enabled;
      if (this.label && text) this.label.textContent = text;
      const sticky = document.querySelector('[data-sticky-add]');
      if (sticky) {
        sticky.disabled = !enabled;
        if (text) sticky.textContent = text;
      }
    }
  }

  /* ---------- Quantity input with +/- buttons ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.querySelectorAll('[data-quantity-change]').forEach((button) => {
        button.addEventListener('click', () => {
          const step = parseInt(button.dataset.quantityChange, 10) || 1;
          const min = parseInt(this.input.min, 10) || 0;
          const current = parseInt(this.input.value, 10) || min;
          this.input.value = Math.max(min, current + step);
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    }
  }

  if (!customElements.get('rotating-word')) customElements.define('rotating-word', RotatingWord);
  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Sticky add-to-cart bar (product page, mobile) ---------- */
  function initStickyBar() {
    const bar = document.querySelector('[data-sticky-bar]');
    const mainButton = document.querySelector('.product__form [data-add-to-cart]');
    if (!bar || !mainButton || !('IntersectionObserver' in window)) return;
    const stickyButton = bar.querySelector('[data-sticky-add]');
    if (stickyButton) {
      stickyButton.addEventListener('click', () => {
        mainButton.scrollIntoView({ block: 'center', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        mainButton.click();
      });
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          bar.hidden = entry.isIntersecting || entry.boundingClientRect.top < 0 === false && entry.boundingClientRect.top > window.innerHeight;
        });
      },
      { threshold: 0 }
    );
    observer.observe(mainButton);
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStickyBar);
  } else {
    initStickyBar();
  }

  /* ---------- Cart page: quantity changes update the cart ---------- */
  let cartTimer = 0;
  document.addEventListener('change', (event) => {
    const target = event.target;
    if (target.matches('[data-auto-submit]') && target.form) {
      target.form.submit();
      return;
    }
    const cartForm = target.closest('[data-cart-form]');
    if (cartForm && target.matches('input[name="updates[]"]')) {
      window.clearTimeout(cartTimer);
      cartTimer = window.setTimeout(() => cartForm.submit(), 350);
    }
  });

  /* ---------- Checkout Champ direct redirect (no app needed) ----------
     When Theme settings > Checkout Champ > "direct redirect" is on, the cart's
     Checkout button carries data-cc-direct-url. On click we read the live cart
     and send the visitor to that page with ?products=EXT<productId>:<qty>;...
     which is the URL format Checkout Champ accepts for synced Shopify products. */
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('button[name="checkout"][data-cc-direct-url]');
      if (!button) return;
      event.preventDefault();
      event.stopPropagation();
      const base = button.dataset.ccDirectUrl;
      const fallback = () => {
        button.disabled = false;
        if (button.form) button.form.submit();
      };
      button.disabled = true;
      fetch(window.theme.routes.cartJs, { headers: { Accept: 'application/json' } })
        .then((response) => response.json())
        .then((cart) => {
          const products = (cart.items || [])
            .map((item) => `EXT${item.product_id}:${item.quantity}`)
            .join(';');
          if (!products) {
            button.disabled = false;
            return;
          }
          const joiner = base.includes('?') ? '&' : '?';
          window.location.href = `${base}${joiner}products=${products}`;
        })
        .catch(fallback);
    },
    true
  );

  /* ---------- Clear the cart when coming back from the Checkout Champ thank-you page ----------
     The thank-you page links to the store with ?clear_cart=1. */
  if (/[?&]clear_cart=1(?:&|$)/.test(window.location.search)) {
    fetch(`${window.theme.routes.cart}/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } })
      .catch(() => {})
      .finally(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete('clear_cart');
        window.history.replaceState({}, '', url.pathname + (url.search || '') + url.hash);
        document.querySelectorAll('[data-cart-count]').forEach((el) => {
          el.textContent = '0';
          el.classList.add('is-empty');
        });
      });
  }
})();
